// MARK://dhk8/pos/features/RelayJobToaster
// Surface global del seguimiento de impresiones por la nube (Print Relay).
// Montado UNA vez en el Shell autenticado (App.tsx). Pinta cada job que está
// siendo seguido por relay-job-tracker como un toast apilado abajo-derecha:
//
//   enviado → imprimiendo → ✅ impreso | ⚠️ reintentando (x/3) | ❌ falló (motivo)
//
// Por qué un surface global: las impresiones en mode 'relay' son fire-and-forget
// desde muchos callsites (cobro, fire-to-kitchen, corte de caja…) y resuelven al
// ENCOLAR, no al imprimir. Este toaster es la única forma de que el usuario vea
// el resultado REAL del papel sin acoplar cada pantalla al ciclo del job.
//
// En mode 'host' (loopback) nunca hay jobs trackeados → el toaster no pinta nada
// (cero footprint). El componente es seguro de montar siempre.

import { Cloud, CheckCircle2, XCircle, Loader2, RotateCw, X } from 'lucide-react'
import { useRelayJobTracker, phaseView, type TrackedJob } from '../lib/bridge/relay-job-tracker.js'

export default function RelayJobToaster() {
  const jobs = useRelayJobTracker((s) => s.jobs)
  const dismiss = useRelayJobTracker((s) => s.dismiss)
  const list = Object.values(jobs).sort((a, b) => a.startedAt.localeCompare(b.startedAt))

  if (list.length === 0) return null

  return (
    <div className="dhk8-relay-toaster" role="status" aria-live="polite" aria-label="Estado de impresiones en la nube">
      {list.map((job) => (
        <RelayJobToast key={job.id} job={job} onDismiss={() => dismiss(job.id)} />
      ))}
      <style>{styles}</style>
    </div>
  )
}

function RelayJobToast({ job, onDismiss }: { job: TrackedJob; onDismiss: () => void }) {
  const view = phaseView(job.phase, job.attempts, job.maxAttempts)

  let icon: React.ReactNode
  switch (view.tone) {
    case 'ok':       icon = <CheckCircle2 size={16} />; break
    case 'fail':     icon = <XCircle size={16} />; break
    case 'retry':    icon = <RotateCw size={16} className="dhk8-relay-toast__spin" />; break
    case 'progress': icon = <Loader2 size={16} className="dhk8-relay-toast__spin" />; break
    default:         icon = <Cloud size={16} />
  }

  return (
    <div className={`dhk8-relay-toast dhk8-relay-toast--${view.tone}`}>
      <span className="dhk8-relay-toast__icon">{icon}</span>
      <div className="dhk8-relay-toast__body">
        <div className="dhk8-relay-toast__line">
          <span className="dhk8-relay-toast__label">{job.label}</span>
          <span className="dhk8-relay-toast__status">{view.label}</span>
        </div>
        {(view.tone === 'fail' || view.tone === 'retry') && job.lastError && (
          <div className="dhk8-relay-toast__err">{job.lastError}</div>
        )}
      </div>
      {/* Cerrar manual solo cuando ya terminó — un toast en progreso no se cierra
          a mano para no perder de vista una impresión en curso. */}
      {job.done && (
        <button type="button" className="dhk8-relay-toast__close" onClick={onDismiss} aria-label="Cerrar aviso">
          <X size={13} />
        </button>
      )}
    </div>
  )
}

const styles = `
  .dhk8-relay-toaster { position:fixed; right:var(--pos-space-5, 20px); bottom:var(--pos-space-5, 20px); display:flex; flex-direction:column; gap:var(--pos-space-2, 8px); z-index:var(--pos-z-toast, 300); max-width:min(360px, calc(100vw - 32px)); pointer-events:none; }
  .dhk8-relay-toast { pointer-events:auto; display:flex; align-items:flex-start; gap:var(--pos-space-3, 12px); padding:var(--pos-space-3, 12px) var(--pos-space-4, 16px); background:var(--pos-elevated, #1E2520); border:1px solid var(--pos-separator-strong, rgba(255,255,255,.14)); border-radius:var(--pos-radius-lg, 12px); box-shadow:var(--pos-shadow-3, 0 8px 20px -6px rgba(0,0,0,.35)); animation:dhk8-relay-toast-in var(--pos-duration-base, 180ms) ease; }
  @keyframes dhk8-relay-toast-in { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
  .dhk8-relay-toast__icon { display:inline-flex; flex-shrink:0; margin-top:1px; color:var(--pos-fg-secondary, #B6BFB8); }
  .dhk8-relay-toast--ok .dhk8-relay-toast__icon { color:var(--pos-success, #8BC34A); }
  .dhk8-relay-toast--fail .dhk8-relay-toast__icon { color:var(--pos-danger, #E5484D); }
  .dhk8-relay-toast--retry .dhk8-relay-toast__icon { color:var(--pos-warning, #F5A623); }
  .dhk8-relay-toast--progress .dhk8-relay-toast__icon { color:var(--pos-info, #0091FF); }
  .dhk8-relay-toast__body { flex:1; min-width:0; display:flex; flex-direction:column; gap:2px; }
  .dhk8-relay-toast__line { display:flex; align-items:baseline; gap:var(--pos-space-2, 8px); justify-content:space-between; }
  .dhk8-relay-toast__label { font-size:var(--pos-text-sm, 13px); font-weight:var(--pos-weight-semibold, 600); color:var(--pos-fg, #E8EFE9); }
  .dhk8-relay-toast__status { font-size:var(--pos-text-xs, 12px); font-weight:var(--pos-weight-medium, 500); white-space:nowrap; }
  .dhk8-relay-toast--ok .dhk8-relay-toast__status { color:var(--pos-success, #8BC34A); }
  .dhk8-relay-toast--fail .dhk8-relay-toast__status { color:var(--pos-danger, #E5484D); }
  .dhk8-relay-toast--retry .dhk8-relay-toast__status { color:var(--pos-warning, #F5A623); }
  .dhk8-relay-toast--progress .dhk8-relay-toast__status { color:var(--pos-info, #0091FF); }
  .dhk8-relay-toast--pending .dhk8-relay-toast__status { color:var(--pos-fg-secondary, #B6BFB8); }
  .dhk8-relay-toast__err { font-size:var(--pos-text-xxs, 11px); color:var(--pos-danger, #E5484D); line-height:1.35; word-break:break-word; }
  .dhk8-relay-toast__close { flex-shrink:0; display:inline-flex; align-items:center; justify-content:center; width:24px; height:24px; padding:0; margin:-2px -4px 0 0; background:transparent; border:none; border-radius:var(--pos-radius-sm, 6px); color:var(--pos-fg-tertiary, #7E8C82); cursor:pointer; transition:color var(--pos-duration-fast, 120ms) ease, background var(--pos-duration-fast, 120ms) ease; }
  .dhk8-relay-toast__close:hover { color:var(--pos-fg, #E8EFE9); background:var(--pos-surface, #161C18); }
  .dhk8-relay-toast__spin { animation:dhk8-relay-toast-spin 1.2s linear infinite; }
  @keyframes dhk8-relay-toast-spin { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }
`
